import { useTranslate } from 'react-admin';
import { Chip, Stack, Typography } from '@mui/material';

import { RebalanceItem } from 'src/pages/admin/services/xbitRelayRebalancesService';

import {
  chainLabel,
  destinationTokenSymbol,
  formatAmount,
  formatUnixMs,
  isTokenSwap,
  sourceTokenSymbol,
  statusChipColor,
} from './helpers';

const SummaryLine = ({ label, value }: { label: string; value: string }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
      {value}
    </Typography>
  </Stack>
);

/** The identical read-only header every rebalance action dialog opens with. */
export const RebalanceSummary = ({ record }: { record: RebalanceItem }) => {
  const t = useTranslate();

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('module.xbitRelayHotWalletBalancer.status')}
        </Typography>
        <Chip
          label={record.status || '-'}
          size="small"
          color={statusChipColor(record.status)}
          variant="outlined"
        />
      </Stack>
      <SummaryLine label={t('module.xbitRelayHotWalletBalancer.rebalanceId')} value={record.id} />
      <SummaryLine
        label={t('module.xbitRelayHotWalletBalancer.provider')}
        value={record.provider || '-'}
      />
      <SummaryLine
        label={t('module.xbitRelayHotWalletBalancer.token')}
        value={
          isTokenSwap(record)
            ? `${sourceTokenSymbol(record)} → ${destinationTokenSymbol(record)}`
            : sourceTokenSymbol(record) || '-'
        }
      />
      <SummaryLine
        label={t('module.xbitRelayHotWalletBalancer.amount')}
        value={`${formatAmount(record.amount)} ${sourceTokenSymbol(record)}`.trim()}
      />
      <SummaryLine
        label={t('module.xbitRelayHotWalletBalancer.minReceived')}
        value={`${formatAmount(record.minReceived)} ${destinationTokenSymbol(record)}`.trim()}
      />
      <SummaryLine
        label={t('module.xbitRelayHotWalletBalancer.route')}
        value={`${chainLabel(record.srcChainId)} → ${chainLabel(record.dstChainId)}`}
      />
      <SummaryLine
        label={t('module.xbitRelayHotWalletBalancer.srcAddress')}
        value={record.srcAddress || '-'}
      />
      <SummaryLine
        label={t('module.xbitRelayHotWalletBalancer.dstAddress')}
        value={record.dstAddress || '-'}
      />
      <SummaryLine
        label={t('module.xbitRelayHotWalletBalancer.createdAt')}
        value={formatUnixMs(record.createdAtUnixMs)}
      />
    </Stack>
  );
};
